import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';
import { NEWTON_MARK_SRC } from '@/utils/nShowcase';

import { ROUTE } from '../../../../routes';
import { useImmersive } from '../../immersiveControl';
import { usePendingFeatureInvoices } from '../../../../hooks/usePendingFeatureInvoices';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import type { FeatureFlagKey } from '../../../../config/featureFlags';
import { railTarget, type FtuxTargetId } from '../../../../features/ftux/targets';
import { RAIL_DOORS, RAIL_GROUP_BREAK, STUDIO_DOORS, type RailDoorKey } from '../../studioMenu';
import { MaskIcon } from '../MaskIcon';
import { WebsiteGlyph } from '../WebsiteGlyph';
import { PlayersGlyph } from '../PlayersGlyph';
import { MarketingGlyph } from '../MarketingGlyph';
import { MoreGlyph } from '../MoreGlyph';
import { MoreMenu } from '../MoreMenu';
import type { MoreMenuItem } from '../MoreMenu';
import {
  Root,
  Inner,
  Measure,
  LogoItem,
  Item,
  MeasureItem,
  ItemIcon,
  ItemLabel,
  LogoGlyph,
  NewtonMark,
  LockOverlay,
  NavDot,
  Sep,
  Spacer,
  MoreWrap,
  MoreTrigger,
} from './SideMenu.styles';

/**
 * Desktop left rail — converted from the `[owners-hub] side-menu` template.
 *
 * Order: logo, then the studio's menu (studioMenu.ts): Home · Stats · Players · ─ · Product · Acquisition ·
 * Operations · ─ · Website · Marketing · spacer · Settings; active/hover recolour comes from NavLink's
 * `.active` + the inline SVG paths. (Wallet is reached from the top bar's balance
 * pill, so it's kept off the rail; the `spacer` floats Settings to the bottom.)
 * (Website settings moved under Settings → `/account/settings/website-settings`.)
 *
 * When the viewport is too short to fit every item (Figma "Web - Small Height -
 * Show more options", node 8927:649), the trailing items collapse into a "More"
 * flyout instead of clipping. How many fit is decided by measuring a hidden,
 * out-of-flow clone of the full list against the rail's (viewport-driven)
 * height — so the result is stable and doesn't oscillate as items move in/out.
 */
const CDN = 'https://micros-t.b-cdn.net';

/** Where the FTUX finds the More trigger (its rail items carry `rail-<key>`, see renderItem). Inert outside an FTUX
 *  step. */
const RAIL_MORE_TARGET: FtuxTargetId = 'rail-more';

type NavEntry = {
  key: RailDoorKey;
  href: string;
  label: string;
  /** Remote SVG, rendered via MaskIcon (CSS mask, recoloured by background-color). */
  icon?: string;
  /** Local inline glyph — takes precedence over `icon` (e.g. the Players glyph). */
  glyph?: React.ReactElement;
  /** Render a separator line before this item (group boundary, both modes). */
  sepBefore?: boolean;
  /** Render the flex spacer before this item (full-height mode only). */
  spacerBefore?: boolean;
  /** Build-ahead feature this entry belongs to; the rail drops it while the flag
   *  is off (see `config/featureFlags`). Absent = always on the rail. */
  flag?: FeatureFlagKey;
};

/** Each rail door's look: a remote SVG drawn through MaskIcon, or a local glyph. */
const RAIL_LOOK: Readonly<Record<RailDoorKey, Pick<NavEntry, 'icon' | 'glyph'>>> = {
  home: { icon: `${CDN}/studio-menu-icon-home-1779968968258.svg` },
  // Stats wears the pulse-line glyph (the files are named the other way round).
  stats: { icon: `${CDN}/studio-menu-icon-pulse-1779968968265.svg` },
  players: { glyph: <PlayersGlyph /> },
  product: { icon: `${CDN}/studio-menu-icon-product-1779968968262.svg` },
  acquisition: { icon: `${CDN}/studio-menu-icon-acquisition-1779968968261.svg` },
  operations: { icon: `${CDN}/studio-menu-icon-operations-1779968968260.svg` },
  website: { glyph: <WebsiteGlyph /> },
  marketing: { glyph: <MarketingGlyph /> },
  settings: { icon: `${CDN}/studio-menu-icon-settings-1779968968253.svg` },
};

/**
 * The full ordered rail (the studio's menu, studioMenu.ts), with its group boundaries: a door that opens a group gets
 * its separator, or the spacer that floats the account group (Settings) to the bottom of the full-height rail.
 * Overflow eats from the end (Settings first), so the primary items survive longest.
 */
const NAV_ITEMS: NavEntry[] = RAIL_DOORS.map((key, index) => {
  const door = STUDIO_DOORS[key];
  const above = index > 0 ? STUDIO_DOORS[RAIL_DOORS[index - 1]] : null;
  const opensGroup = above !== null && above.group !== door.group;
  return {
    key,
    href: door.href,
    label: door.label,
    flag: door.flag,
    ...RAIL_LOOK[key],
    sepBefore: opensGroup && RAIL_GROUP_BREAK[door.group] === 'line',
    spacerBefore: opensGroup && RAIL_GROUP_BREAK[door.group] === 'foot',
  };
});

/**
 * Owners-Hub brand mark — the three ascending bars. Drawn inline (not via a
 * remote `<img>`) so it recolours with the theme: the `LogoGlyph` wrapper feeds
 * `currentColor` from the heading-ink token (near-white on the dark rail, deep
 * ink on the light theme's white rail). The artwork's full-bounds clip-path is
 * dropped — it's a no-op, and dropping it keeps the live + measure copies from
 * sharing a duplicate element id.
 */
const BrandLogo = (): React.ReactElement => (
  <LogoGlyph aria-hidden="true">
    <svg width="24" height="28" viewBox="0 0 110 128" fill="none">
      <path
        fill="currentColor"
        d="M0 35.8208V92.1883C0.001 92.8779 0.163 93.5578 0.474 94.1736C0.784 94.7893 1.235 95.3239 1.789 95.7347C2.343 96.1454 2.985 96.421 3.665 96.5392C4.344 96.6575 5.042 96.6152 5.702 96.4158L21.731 91.5682C22.639 91.2936 23.435 90.7345 24.001 89.9733C24.567 89.2121 24.873 88.2891 24.874 87.3407V40.302C24.874 39.3363 24.558 38.3973 23.974 37.6282C23.39 36.8592 22.57 36.3023 21.64 36.0427L5.61 31.5686C4.954 31.3856 4.264 31.3569 3.595 31.4845C2.925 31.6121 2.294 31.8927 1.751 32.3043C1.208 32.7159 0.768 33.2475 0.464 33.8574C0.16 34.4674 0.001 35.1393 0 35.8208Z"
      />
      <path
        fill="currentColor"
        d="M42.1875 18.0981V109.896C42.1872 110.556 42.3511 111.206 42.6646 111.787C42.978 112.368 43.4311 112.861 43.983 113.224C44.5349 113.586 45.1682 113.805 45.8259 113.861C46.4836 113.917 47.1449 113.809 47.7503 113.546L64.6711 106.199C65.3825 105.89 65.9881 105.38 66.4134 104.731C66.8387 104.083 67.0652 103.324 67.0649 102.549V24.7389C67.0636 23.9392 66.8211 23.1583 66.3693 22.4985C65.9174 21.8386 65.2771 21.3303 64.5319 21.0398C60.5862 19.5038 52.8832 16.5005 47.6358 14.4078C47.0321 14.1671 46.3787 14.0781 45.7326 14.1484C45.0866 14.2188 44.4676 14.4465 43.93 14.8115C43.3923 15.1766 42.9523 15.6679 42.6486 16.2424C42.3448 16.8169 42.1865 17.4571 42.1875 18.107Z"
      />
      <path
        fill="currentColor"
        d="M84.2188 4.29442V123.704C84.2189 124.455 84.4165 125.194 84.792 125.845C85.1674 126.496 85.7074 127.037 86.3579 127.414C87.0084 127.791 87.7464 127.99 88.4982 127.992C89.2499 127.994 89.9889 127.798 90.641 127.424L106.942 118.072C107.597 117.697 108.141 117.155 108.519 116.501C108.897 115.848 109.096 115.107 109.096 114.352V13.0719C109.096 12.2961 108.886 11.5348 108.488 10.8691C108.09 10.2034 107.518 9.65828 106.834 9.29175C103.186 7.33651 95.8761 3.41368 90.5512 0.523087C89.898 0.168618 89.164 -0.00999893 88.421 0.00475107C87.678 0.0195011 86.9516 0.22712 86.313 0.607236C85.6745 0.987352 85.1457 1.52691 84.7785 2.17301C84.4114 2.8191 84.2185 3.55129 84.2188 4.29442Z"
      />
    </svg>
  </LogoGlyph>
);

/**
 * The rail's top mark: the Newton N while the Newton showcase is on (the owner's
 * `n_showcase` preference or the local switch, see `utils/nShowcase`), the Owners
 * Hub bars otherwise. Decorative either way (the home link around
 * it carries the accessible name).
 */
const RailLogo = ({ newton }: { newton: boolean }): React.ReactElement =>
  newton ? <NewtonMark src={NEWTON_MARK_SRC} alt="" aria-hidden="true" draggable={false} /> : <BrandLogo />;

/**
 * Padlock drawn over a locked nav icon. Strokes use `currentColor` so the
 * `LockOverlay` recolours it per theme (near-white on dark, deep ink on light).
 * The artwork's full-bounds 16×16 clip-path is dropped — it's a no-op and dropping
 * it avoids duplicate element ids across the rail's many lock glyphs.
 */
const LockGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M1.33594 10.6667C1.33594 8.78105 1.33594 7.83824 1.92172 7.25245C2.50751 6.66666 3.45032 6.66666 5.33594 6.66666H10.6693C12.5549 6.66666 13.4977 6.66666 14.0835 7.25245C14.6693 7.83824 14.6693 8.78105 14.6693 10.6667C14.6693 12.5523 14.6693 13.4951 14.0835 14.0809C13.4977 14.6667 12.5549 14.6667 10.6693 14.6667H5.33594C3.45032 14.6667 2.50751 14.6667 1.92172 14.0809C1.33594 13.4951 1.33594 12.5523 1.33594 10.6667Z"
      stroke="currentColor"
      strokeWidth="1.14286"
    />
    <circle cx="7.9974" cy="10.6667" r="1.33333" stroke="currentColor" strokeWidth="1.14286" />
    <path
      d="M4 6.66667V5.33333C4 3.12419 5.79086 1.33333 8 1.33333C9.86384 1.33333 11.4299 2.6081 11.874 4.33333"
      stroke="currentColor"
      strokeWidth="1.14286"
      strokeLinecap="round"
    />
  </svg>
);

const renderItem = (entry: NavEntry, label: string, locked: boolean, alert = false, forceActive = false): React.ReactElement => (
  <Item
    key={entry.key}
    to={entry.href}
    // `forceActive` lights an entry whose route doesn't match its own href — Stats stays highlighted on
    // /account/pulse (Pulse is a tab of Stats). NavLink won't add `.active` there, so we add it.
    className={[locked ? 'locked' : '', forceActive ? 'active' : ''].filter(Boolean).join(' ') || undefined}
    tabIndex={locked ? -1 : undefined}
    aria-disabled={locked || undefined}
    // Where the FTUX finds this entry (inert outside an FTUX step). The hidden Measure clone carries none, so no
    // duplicate is laid out.
    data-ftux={railTarget(entry.key)}
  >
    <ItemIcon className="icon">
      {entry.glyph ?? <MaskIcon className="icon-mask" src={entry.icon ?? ''} />}
      {/* Attention dot (e.g. a Product feature with a payment to finish). Hidden on the
          locked rail, where every entry is inert. */}
      {alert && !locked ? <NavDot aria-hidden="true" /> : null}
      {locked ? (
        <LockOverlay aria-hidden="true" data-lock="">
          <LockGlyph />
        </LockOverlay>
      ) : null}
    </ItemIcon>
    <ItemLabel className="txt" text={label} />
  </Item>
);

export type SideMenuProps = {
  /**
   * Render the rail in its onboarding-locked state: every nav entry (and the More
   * trigger) is inert — non-clickable, non-focusable — with its icon frosted under
   * a padlock and its label dimmed to 30%. The brand logo at the top is exempt and
   * stays fully visible. Default `false` (the normal interactive rail). Set by
   * `CustomizerLayout` while the owner has no other brand to navigate to.
   */
  locked?: boolean;
};

export const SideMenu = ({ locked = false }: SideMenuProps): React.ReactElement => {
  const { immersive } = useImmersive();
  const { t } = useTranslation();
  const newtonShowcase = useAppSelector(selectNShowcase);

  // A Product feature with an unpaid upgrade invoice lights an attention dot on the Product
  // rail entry (same signal as the Product-page badges + the home Spotlight upsell). Scoped
  // to the active brand; clears the instant the payment is finished / cancelled.
  const pendingFeatureInvoices = usePendingFeatureInvoices();
  const productHasAlert = pendingFeatureInvoices.size > 0;

  // Translate the rail labels at the render site (the literals below are what the extractor harvests). Keyed by the
  // door, so a door added to the studio's menu (studioMenu.ts) fails the typecheck until its label is here: never a
  // door left in English in the other languages.
  const navLabels: Readonly<Record<RailDoorKey, string>> = {
    home: t('Home'),
    stats: t('Stats'),
    product: t('Product'),
    acquisition: t('Acquisition'),
    operations: t('Operations'),
    website: t('Website'),
    marketing: t('Marketing'),
    players: t('Players'),
    settings: t('Settings'),
  };
  const labelFor = (entry: NavEntry): string => navLabels[entry.key];

  // The rail as it actually stands, gated entries dropped. Everything below
  // measures and slices THIS, not the declaration: the overflow maths counts
  // items against the available height, and counting one that is not rendered
  // collapses the rail a step too early. The gate object's identity changes when
  // a reveal flips, so the list — and the re-measure that follows it — is live.
  const gate = useFeatureGate();
  const navItems = React.useMemo(() => NAV_ITEMS.filter((entry) => gate.isEnabled(entry.flag)), [gate]);

  const rootRef = React.useRef<HTMLElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = React.useState(navItems.length);
  const [open, setOpen] = React.useState(false);

  const reactId = React.useId();
  const triggerId = `sm-more-trigger-${reactId}`;
  const menuId = `sm-more-menu-${reactId}`;

  // Decide how many leading items fit. Available height is the rail's content
  // box (viewport-driven, independent of what we render → no feedback loop); the
  // hidden Measure clone gives each item's natural bottom offset.
  const recompute = React.useCallback(() => {
    const inner = innerRef.current;
    const measure = measureRef.current;
    if (!inner || !measure) return;

    const available = inner.clientHeight;
    // Hidden (mobile / not yet laid out) → don't collapse; show everything.
    if (available <= 0) {
      setVisibleCount(navItems.length);
      return;
    }

    const itemEls = measure.querySelectorAll<HTMLElement>('[data-mi]');
    const moreEl = measure.querySelector<HTMLElement>('[data-more]');
    if (itemEls.length !== navItems.length || !moreEl) return;

    const bottoms = Array.from(itemEls, (el) => el.offsetTop + el.offsetHeight);
    const naturalBottom = bottoms[bottoms.length - 1];

    // Everything fits → full-height layout, no flyout.
    if (naturalBottom <= available) {
      setVisibleCount(navItems.length);
      return;
    }

    // Otherwise: largest k where the first k items + the More trigger fit.
    const moreH = moreEl.offsetHeight;
    let visible = 1;
    for (let k = navItems.length - 1; k >= 1; k -= 1) {
      if (bottoms[k - 1] + moreH <= available) {
        visible = k;
        break;
      }
    }
    setVisibleCount(visible);
    // Keyed on the LENGTH of the gated rail: revealing a section adds an item to
    // measure, and the count decided before it appeared would collapse the rail
    // one item early (or leave a stale "More" with nothing behind it).
  }, [navItems.length]);

  React.useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Re-measure when the rail (viewport) or the clone (e.g. late-loading font)
  // changes size. rAF-debounced so the ResizeObserver never loops.
  React.useEffect(() => {
    let raf = 0;
    const schedule = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };
    const ro = new ResizeObserver(schedule);
    if (rootRef.current) ro.observe(rootRef.current);
    if (measureRef.current) ro.observe(measureRef.current);
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [recompute]);

  const overflow = visibleCount < navItems.length;

  // If the rail grows back to fit everything, drop the (now-gone) flyout.
  React.useEffect(() => {
    if (!overflow && open) setOpen(false);
  }, [overflow, open]);

  // Close on outside-click / Escape while open.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      // The flyout is portaled to <body> (outside wrapRef), so test both.
      if (!wrapRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visibleItems = overflow ? navItems.slice(0, visibleCount) : navItems;
  // The flyout's RowLabel re-translates its `label` via <Text>; passing the
  // already-translated value (harvested above) is harmless and keeps the source
  // of truth here at the render site.
  const overflowItems: MoreMenuItem[] = overflow
    ? navItems.slice(visibleCount).map((entry) => ({ ...entry, label: labelFor(entry) }))
    : [];

  // Light up "More" (active accent) when the current route is one of the items
  // tucked inside the flyout — so the rail still shows where you are. Matches
  // NavLink's default activeness (exact path or a nested sub-path).
  const { pathname } = useLocation();
  const activeInMore = overflowItems.some(
    (it) => pathname === it.href || pathname.startsWith(`${it.href}/`),
  );

  const railNodes: React.ReactNode[] = [];
  visibleItems.forEach((entry, i) => {
    if (i > 0) {
      if (entry.sepBefore) railNodes.push(<Sep key={`sep-${entry.key}`} />);
      else if (entry.spacerBefore && !overflow) railNodes.push(<Spacer key={`spc-${entry.key}`} />);
    }
    railNodes.push(
      renderItem(
        entry,
        labelFor(entry),
        locked,
        entry.key === 'product' && productHasAlert,
        entry.key === 'stats' && pathname.startsWith('/account/pulse'),
      ),
    );
  });

  return (
    <Root ref={rootRef} $immersive={immersive}>
      <Inner ref={innerRef}>
        <LogoItem
          to={ROUTE.home()}
          aria-label={t('Owners Hub home')}
          className={locked ? 'locked' : undefined}
          tabIndex={locked ? -1 : undefined}
          aria-disabled={locked || undefined}
          onClick={locked ? (e) => e.preventDefault() : undefined}
        >
          <RailLogo newton={newtonShowcase} />
        </LogoItem>

        {railNodes}

        {overflow ? (
          <MoreWrap ref={wrapRef}>
            <MoreTrigger
              id={triggerId}
              type="button"
              className={locked ? 'locked' : undefined}
              tabIndex={locked ? -1 : undefined}
              aria-disabled={locked || undefined}
              $open={open}
              $active={activeInMore}
              aria-haspopup={locked ? undefined : 'menu'}
              aria-expanded={locked ? undefined : open}
              aria-controls={locked ? undefined : menuId}
              onClick={locked ? undefined : () => setOpen((o) => !o)}
              data-ftux={RAIL_MORE_TARGET}
            >
              <ItemIcon className="icon">
                <MoreGlyph />
                {locked ? (
                  <LockOverlay aria-hidden="true" data-lock="">
                    <LockGlyph />
                  </LockOverlay>
                ) : null}
              </ItemIcon>
              <ItemLabel className="txt" text="More" />
            </MoreTrigger>

            {locked ? null : (
              <MoreMenu
                open={open}
                items={overflowItems}
                anchorRef={wrapRef}
                surfaceRef={menuRef}
                onNavigate={() => setOpen(false)}
                labelledBy={triggerId}
                id={menuId}
              />
            )}
          </MoreWrap>
        ) : null}
      </Inner>

      {/* Hidden measurement clone — full list (collapsed form) + a sample More. */}
      <Measure ref={measureRef} aria-hidden>
        <LogoItem to={ROUTE.home()} tabIndex={-1} aria-hidden>
          <RailLogo newton={newtonShowcase} />
        </LogoItem>
        {navItems.map((entry, i) => (
          <React.Fragment key={`m-${entry.key}`}>
            {i > 0 && entry.sepBefore ? <Sep /> : null}
            <MeasureItem data-mi="">
              <ItemIcon className="icon">{entry.glyph ?? <MaskIcon className="icon-mask" src={entry.icon ?? ''} />}</ItemIcon>
              <ItemLabel className="txt" text={labelFor(entry)} />
            </MeasureItem>
          </React.Fragment>
        ))}
        <MeasureItem data-more="">
          <ItemIcon className="icon">
            <MoreGlyph />
          </ItemIcon>
          <ItemLabel className="txt" text="More" />
        </MeasureItem>
      </Measure>
    </Root>
  );
};

export default SideMenu;
